<template>
  <div class="modal-overlay">
    <div class="modal-background" @click="$emit('close')" />
    <div class="modal">
      <div class="modal-content">
        <div class="modal-header">
          <h1 class="modal-title">{{ ticket.name }}</h1>
          <button class="close-button" @click="$emit('close')">
            <v-icon>close</v-icon>
          </button>
        </div>

        <div class="stats-row">
          <div class="stat-item stat-sold">
            <span class="stat-label">{{ adminText('workflowsAll.sold_e6560d9', 'Sold') }}</span>
            <span class="stat-value">{{ ticket.sold }}</span>
          </div>
          <div class="stat-item stat-price">
            <span class="stat-label">{{ adminText('workflowsAll.price_3e8248e', 'Price') }}</span>
            <span class="stat-value">{{ formattedPrice }}</span>
          </div>
          <div class="stat-item stat-date">
            <div class="stat-label-row">
              <span class="stat-label">{{ adminText('workflowsAll.start_date_ff99f5b', 'Start date') }}</span>
              <button class="edit-icon-button" @click="openPicker('startPicker')">
                <v-icon size="12">edit</v-icon>
              </button>
            </div>
            <span class="stat-value">{{ formattedFrom }}</span>
            <span v-if="dateError" class="date-error">{{ adminText('workflowsAll.must_be_before_end_date_65ba806', 'Must be before end date.') }}</span>
          </div>
          <div class="stat-item stat-date">
            <div class="stat-label-row">
              <span class="stat-label">{{ adminText('workflowsAll.end_date_89d10cd', 'End date') }}</span>
              <button class="edit-icon-button" @click="openPicker('endPicker')">
                <v-icon size="12">edit</v-icon>
              </button>
            </div>
            <span class="stat-value">{{ formattedTo }}</span>
            <span v-if="dateError" class="date-error">{{ adminText('workflowsAll.must_be_after_start_date_efcfa6b', 'Must be after start date.') }}</span>
          </div>
        </div>
        <div class="hidden-pickers">
          <datetime-field
            ref="startPicker"
            v-model="form.from"
            :label="adminText('workflowsAll.start_date_ff99f5b', 'Start date')"
            :max="form.to ? form.to.toString() : null"
          />
          <datetime-field
            ref="endPicker"
            v-model="form.to"
            :label="adminText('workflowsAll.end_date_89d10cd', 'End date')"
            :min="form.from ? form.from.toString() : null"
          />
        </div>

        <div class="input-row">
          <text-field
            v-model.number="form.itemMeta.min"
            type="number"
            step="1"
            :label="adminText('workflowsAll.per_order_min_82dda70', 'Per order min')"
            :validator="$v.form.itemMeta.min"
            :error-messages="{
              minValue: adminText('workflowsAll.minimum_value_is_0_a8f2787', 'Minimum value is 0.'),
              maxValue: adminText('workflowsAll.maximum_value_is_10_8206df3', 'Maximum value is 10.'),
              notGreaterThanMax: adminText('workflowsAll.min_must_be_lower_than_or_equal_to_max_413910d', 'Min must be lower than or equal to max.'),
            }"
            :hint="adminText('workflowsAll.minimum_of_tickets_a_customer_must_choose_whe_3edaa96', 'Minimum of tickets a customer must choose when purchasing.')"
            persistent-hint
          />
          <text-field
            v-model.number="form.itemMeta.max"
            type="number"
            step="1"
            :label="adminText('workflowsAll.per_order_max_959bb27', 'Per order max')"
            :validator="$v.form.itemMeta.max"
            :error-messages="{
              minValue: adminText('workflowsAll.minimum_value_is_1_c0195ad', 'Minimum value is 1.'),
              maxValue: adminText('workflowsAll.maximum_value_is_10_8206df3', 'Maximum value is 10.'),
              notLessThanMin: adminText('workflowsAll.max_must_be_greater_than_or_equal_to_min_8852fa8', 'Max must be greater than or equal to min.'),
            }"
            :hint="adminText('workflowsAll.maximum_of_tickets_a_customer_can_choose_when_39e34b3', 'Maximum of tickets a customer can choose when purchasing. Maximum limit can be set to 10.')"
            persistent-hint
          />
        </div>
        <p v-if="isSharedTable" class="shared-table-hint">
          {{ adminText('workflowsAll.shared_table_limits_hint', 'For shared tables, max sets the seats per table. Only existing packages within min/max remain allowed.') }}
        </p>
        <p v-if="actionError" class="update-error" role="alert">{{ actionError }}</p>
      </div>

      <div class="modal-actions">
        <main-button depressed :loading="saving" @click="saveChanges">
          {{ adminText('workflowsAll.spremi_promjene_d866cf5', 'SPREMI PROMJENE') }}
        </main-button>
      </div>
    </div>
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import moment from 'moment'
import { minValue, maxValue } from 'vuelidate/lib/validators'
import { updateOrderItem } from '@/http'

export default {
  name: 'edit-ticket-modal',
  mixins: [WorkflowDisplay],
  props: {
    ticket: {
      type: Object,
      required: true,
    },
    organizationSlug: {
      type: String,
      required: true,
    },
  },
  data() {
    const itemMeta = this.ticket.itemMeta
      ? typeof this.ticket.itemMeta === 'string'
        ? JSON.parse(this.ticket.itemMeta)
        : { ...this.ticket.itemMeta }
      : {}

    return {
      form: {
        from: this.ticket.rawFrom || this.ticket.from,
        to: this.ticket.rawTo || this.ticket.to,
        itemMeta: {
          min: itemMeta.min || null,
          max: itemMeta.max || null,
          ...itemMeta,
        },
      },
      saving: false,
      actionError: null,
    }
  },
  validations() {
    return {
      form: {
        itemMeta: {
          min: {
            minValue: minValue(0),
            maxValue: maxValue(10),
            notGreaterThanMax: value =>
              value === null ||
              value === '' ||
              this.form.itemMeta.max === null ||
              this.form.itemMeta.max === '' ||
              Number(value) <= Number(this.form.itemMeta.max),
          },
          max: {
            minValue: minValue(1),
            maxValue: maxValue(10),
            notLessThanMin: value =>
              value === null ||
              value === '' ||
              this.form.itemMeta.min === null ||
              this.form.itemMeta.min === '' ||
              Number(value) >= Number(this.form.itemMeta.min),
          },
        },
      },
    }
  },
  computed: {
    isSharedTable() {
      return !!(this.form.itemMeta.table && this.form.itemMeta.sharedTable && this.form.itemMeta.sharedTable.enabled)
    },
    formattedPrice() {
      const price = this.ticket.price
      const currency = this.ticket.currency || 'EUR'

      return `${price} ${currency}`
    },
    formattedFrom() {
      return this.form.from ? moment(this.form.from).format('DD.MM.YYYY. HH:mm') : '-'
    },
    formattedTo() {
      return this.form.to ? moment(this.form.to).format('DD.MM.YYYY. HH:mm') : '-'
    },
    dateError() {
      if (
        this.form.from &&
        this.form.to &&
        moment(this.form.to).isSameOrBefore(moment(this.form.from))
      ) {
        return this.adminText('workflowsAll.end_date_must_be_after_start_date_f5bdfd1', 'End date must be after start date.')
      }

      return null
    },
  },
  methods: {
    openPicker(ref) {
      this.$refs[ref].display = true
    },
    async saveChanges() {
      if (this.saving) return
      this.$v.$touch()
      if (this.$v.$invalid || this.dateError) return
      this.saving = true
      this.actionError = null
      try {
        const input = {
          name: this.ticket.name,
          from: this.form.from,
          to: this.form.to,
          price: this.ticket.price,
          initialQuantity: this.ticket.initialQuantity,
          type: this.ticket.type || 'ticket',
          currency: this.ticket.currency,
          itemMeta: this.form.itemMeta,
        }
        if (input.itemMeta && input.itemMeta.max === '') input.itemMeta.max = null
        if (input.itemMeta && input.itemMeta.min === '') input.itemMeta.min = null

        await updateOrderItem(input, this.ticket.id, this.organizationSlug)
        this.$emit('saved')
        this.$emit('close')
      } catch (e) {
        const networkError = e && e.networkError
        const errors = [
          ...((e && e.graphQLErrors) || []),
          ...((networkError && networkError.result && networkError.result.errors) || []),
        ]
        const messages = errors.map(error => error && error.message)
        messages.push(networkError && networkError.message, e && e.message)
        this.actionError = messages.find(message => typeof message === 'string' && message.trim() &&
          !/^(?:(?:Network|GraphQL) error:\s*)?(?:undefined|null)?$/i.test(message.trim())) ||
          this.adminText('workflowsAll.something_went_wrong_please_try_again_9a3ea05', 'Something went wrong. Please try again.')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  padding: 12px;
}

.modal-background {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal {
  background-color: white;
  position: relative;
  width: 100%;
  max-width: 732px;
  max-height: calc(100vh - 24px);
  border-radius: 12px;
  overflow-y: auto;
  box-shadow: 0px 0px 24px rgba(51, 51, 51, 0.2);
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 24px 24px 0;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-title {
  font-family: 'Raleway', sans-serif;
  font-weight: 600;
  font-size: 24px;
  line-height: 1.2;
  letter-spacing: -0.72px;
  color: #333;
  font-feature-settings: 'lnum', 'pnum';
}

.close-button {
  background: white;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stats-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.stat-item {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.stat-sold {
  width: 100px;
  flex-shrink: 0;
}

.stat-price {
  flex: 1;
}

.stat-label {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 14px;
  line-height: 1.2;
  color: #666;
}

.stat-label-row {
  display: flex;
  align-items: center;
}

.edit-icon-button {
  background: white;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.stat-value {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 20px;
  font-weight: 400;
  line-height: 1.5;
  color: #333;
  white-space: nowrap;
}

.stat-date {
  position: relative;
}

.date-error {
  position: absolute;
  top: 100%;
  left: 0;
  color: #db384c;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 12px;
  white-space: nowrap;
}

.hidden-pickers {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  pointer-events: none;
}

.input-row {
  display: flex;
  gap: 16px;
}

.input-row > * {
  flex: 1;
}

.shared-table-hint {
  margin: 0;
  color: #666;
  overflow-wrap: anywhere;
}

.update-error {
  margin: 0;
  padding: 12px;
  border: 1px solid #efb0b0;
  border-radius: 8px;
  background: #fff4f4;
  color: #9f2424;
  overflow-wrap: anywhere;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 32px 24px 24px;
}

@media screen and (max-width: 700px) {
  .stats-row {
    flex-wrap: wrap;
  }

  .input-row {
    flex-direction: column;
  }
}
</style>
